import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FilterState, FilterOptions } from '../types/plant-filter.types'

interface UsePlantFiltersOptions {
  expandAllInitially?: boolean
}

export const usePlantFilters = (
  initialFilterOptions: FilterOptions,
  setSearchTerm: (searchTerm: string) => void,
  hookOptions?: UsePlantFiltersOptions
) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState<FilterState>(() => {
    const initialState: FilterState = {
      category: [],
      flowerColor: [],
      soilType: [],
      sunExposure: [],
      form: [],
      habit: [],
      leafType: [],
      soilMoisture: [],
      soilPh: [],
      features: [],
      gardenTypes: [],
      usage: [],
      growthRate: [],
      leafColor: [],
      floweringSeason: [],
      fruits: [],
      fruitingSeason: [],
      hardiness: [],
      heightMin: '',
      heightMax: '',
      spreadMin: '',
      spreadMax: ''
    }

    Object.keys(initialState).forEach(key => {
      const paramValue = searchParams.get(key)
      if (paramValue) {
        if (key.includes('Min') || key.includes('Max')) {
          (initialState[key as keyof FilterState] as any) = paramValue
        } else {
          (initialState[key as keyof FilterState] as any) = paramValue.split(',')
        }
      }
    })

    return initialState
  })

  const [expandedFilters, setExpandedFilters] = useState<Record<string, boolean>>(() => {
    const defaultFilterExpansionState: Record<string, boolean> = {
      category: false,
      form: false,
      growthRate: false,
      habit: false,
      leafColor: false,
      leafType: false,
      flowerColor: false,
      floweringSeason: false,
      fruits: false,
      fruitingSeason: false,
      sunExposure: false,
      soilMoisture: false,
      soilPh: false,
      soilType: false,
      features: false,
      gardenTypes: false,
      usage: false,
      hardiness: false,
      dimensions: false
    }

    if (hookOptions?.expandAllInitially) {
      const allExpanded: Record<string, boolean> = {}
      Object.keys(defaultFilterExpansionState).forEach(key => {
        allExpanded[key] = true
      })
      return allExpanded
    }
    return defaultFilterExpansionState
  })

  const toggleFilter = (filterName: string) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }))
  }

  const updateUrlWithFilters = (newFilters: FilterState) => {
    const newParams = new URLSearchParams()

    Object.entries(newFilters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        newParams.set(key, value.join(','))
      } else if (typeof value === 'string' && value !== '') {
        newParams.set(key, value)
      }
    })

    setSearchParams(newParams)
  }

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prevFilters => {
      const filterValue = prevFilters[filterType as keyof typeof prevFilters]
      const currentValues = Array.isArray(filterValue) ? [...filterValue] : []

      const newFilters = {
        ...prevFilters,
        [filterType]: currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value]
      }

      updateUrlWithFilters(newFilters)
      return newFilters
    })
  }

  const handleNumericFilterChange = (filterType: string, value: string) => {
    setFilters(prevFilters => {
      const newFilters = {
        ...prevFilters,
        [filterType]: value
      }
      updateUrlWithFilters(newFilters)
      return newFilters
    })
  }

  const clearAllFilters = () => {
    const emptyFilters: FilterState = {
      category: [],
      flowerColor: [],
      soilType: [],
      sunExposure: [],
      form: [],
      habit: [],
      leafType: [],
      soilMoisture: [],
      soilPh: [],
      features: [],
      gardenTypes: [],
      usage: [],
      growthRate: [],
      leafColor: [],
      floweringSeason: [],
      fruits: [],
      fruitingSeason: [],
      hardiness: [],
      heightMin: '',
      heightMax: '',
      spreadMin: '',
      spreadMax: ''
    }
    setFilters(emptyFilters)
    setSearchParams(new URLSearchParams())
    setSearchTerm('')
    setExpandedFilters(() => {
      const defaultFilterExpansionState: Record<string, boolean> = {
        category: false,
        form: false,
        growthRate: false,
        habit: false,
        leafColor: false,
        leafType: false,
        flowerColor: false,
        floweringSeason: false,
        fruits: false,
        fruitingSeason: false,
        sunExposure: false,
        soilMoisture: false,
        soilPh: false,
        soilType: false,
        features: false,
        gardenTypes: false,
        usage: false,
        hardiness: false,
        dimensions: false
      }
      if (hookOptions?.expandAllInitially) {
        const allExpanded: Record<string, boolean> = {}
        Object.keys(defaultFilterExpansionState).forEach(key => allExpanded[key] = true)
        return allExpanded
      }
      const collapsedState: Record<string, boolean> = {}
      Object.keys(defaultFilterExpansionState).forEach(key => collapsedState[key] = false)
      return collapsedState
    })
  }

  return {
    filters,
    expandedFilters,
    toggleFilter,
    handleFilterChange,
    handleNumericFilterChange,
    clearAllFilters
  }
}
